import dayjs, { Dayjs } from 'dayjs';
import type { AllPlanScheduleType, ScheduledAction } from '@demind-inc/core';

import { safeLocalStorage } from '../utils/safeLocalStorage';
import { scheduleFallsOnDate, type AllPlanDatedSchedule } from './allPlanScheduleDate.helpers';

export const AUTO_ALL_PLAN_STORAGE_KEY = '@auto_all_plan_suggestions_v1';

const getScheduleId = (schedule: ScheduledAction, fallbackIndex = 0): string =>
  String(schedule.id ?? `plan-${fallbackIndex}`);

const ALL_PLAN_SCHEDULE_TYPES: AllPlanScheduleType[] = [
  'meal',
  'sleep',
  'workout',
  'todo',
  'routine',
];

export const isAllPlanScheduleType = (value: unknown): value is AllPlanScheduleType =>
  typeof value === 'string' && (ALL_PLAN_SCHEDULE_TYPES as readonly string[]).includes(value);

/** Per-date Plan My Day cache entry (no date key — keyed by plansByDate). */
export type AutoAllPlanCacheEntry = {
  lastRunAtISO: string;
  signature: string;
  schedules: unknown[];
  plannedTypes?: AllPlanScheduleType[];
  summary: string | null;
  acceptedIds?: string[];
  autoAcceptedIds?: string[];
  discardedIds?: string[];
  lastRunTodoIds?: string[];
  lastRunRoutineIds?: string[];
  /** Set while a Plan My Day job is still generating. Visible schedules are
   * cleared so a refresh cannot paint this date's previous result. */
  pendingJobId?: string;
  /** The entry that was visible before `pendingJobId` was set. Not rendered. */
  stashedEntry?: Omit<AutoAllPlanCacheEntry, 'stashedEntry' | 'pendingJobId'>;
};

/** Legacy single-date blob. Migrated on read into AutoAllPlanCacheStore. */
export type AutoAllPlanCacheV1 = AutoAllPlanCacheEntry & {
  date: string;
};

/**
 * Multi-date Plan My Day cache. Stores today and future plans (e.g. plan-for-tomorrow)
 * so next-morning sleep-sync auto-replan can find the former tomorrow entry as today.
 */
export type AutoAllPlanCacheStore = {
  version: 2;
  plansByDate: Record<string, AutoAllPlanCacheEntry>;
};

const emptyCacheStore = (): AutoAllPlanCacheStore => ({
  version: 2,
  plansByDate: {},
});

const normalizeScheduleKeyPart = (value?: string | null) => (value ?? '').trim().toLowerCase();

const getScheduleDedupeKey = (schedule: ScheduledAction) => {
  const taskId = schedule.todoFrom?.taskId ?? '';
  const routineId = schedule.routineFrom?.routineActivityId ?? '';

  return [
    schedule.eventFrom ?? '',
    schedule.actionType ?? '',
    normalizeScheduleKeyPart(schedule.name),
    normalizeScheduleKeyPart(schedule.startDate),
    normalizeScheduleKeyPart(schedule.endDate),
    normalizeScheduleKeyPart(schedule.categoryId),
    taskId,
    routineId,
  ].join('|');
};

export const dedupeAllPlanScheduledActions = (schedules: ScheduledAction[]) => {
  const dedupedByKey = new Map<string, ScheduledAction>();
  schedules.forEach((schedule) => {
    dedupedByKey.set(getScheduleDedupeKey(schedule), schedule);
  });
  return Array.from(dedupedByKey.values());
};

export const remapAllPlanDecisionIdsForDedupedSchedules = ({
  originalSchedules,
  dedupedSchedules,
  decisionIds,
}: {
  originalSchedules: ScheduledAction[];
  dedupedSchedules: ScheduledAction[];
  decisionIds?: string[];
}) => {
  if (!Array.isArray(decisionIds) || !decisionIds.length) {
    return [];
  }

  const decisionIdSet = new Set(decisionIds);
  const decisionsByDedupeKey = new Set<string>();
  originalSchedules.forEach((schedule, index) => {
    if (decisionIdSet.has(getScheduleId(schedule, index))) {
      decisionsByDedupeKey.add(getScheduleDedupeKey(schedule));
    }
  });

  return dedupedSchedules.reduce((acc, schedule, index) => {
    if (decisionsByDedupeKey.has(getScheduleDedupeKey(schedule))) {
      acc.push(getScheduleId(schedule, index));
    }
    return acc;
  }, [] as string[]);
};

export const parseCacheStore = (raw: string | null): AutoAllPlanCacheStore => {
  if (!raw) {
    return emptyCacheStore();
  }

  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== 'object') {
      return emptyCacheStore();
    }

    const asStore = parsed as Partial<AutoAllPlanCacheStore> & Partial<AutoAllPlanCacheV1>;
    if (
      asStore.version === 2 &&
      asStore.plansByDate &&
      typeof asStore.plansByDate === 'object' &&
      !Array.isArray(asStore.plansByDate)
    ) {
      return {
        version: 2,
        plansByDate: asStore.plansByDate,
      };
    }

    // Migrate legacy V1 single-date blob.
    if (typeof asStore.date === 'string' && asStore.date) {
      const { date, ...entry } = asStore as AutoAllPlanCacheV1;
      return {
        version: 2,
        plansByDate: {
          [date]: {
            lastRunAtISO: entry.lastRunAtISO ?? '',
            signature: entry.signature ?? '',
            schedules: Array.isArray(entry.schedules) ? entry.schedules : [],
            plannedTypes: entry.plannedTypes,
            summary: entry.summary ?? null,
            acceptedIds: entry.acceptedIds,
            autoAcceptedIds: entry.autoAcceptedIds,
            discardedIds: entry.discardedIds,
            lastRunTodoIds: entry.lastRunTodoIds,
            lastRunRoutineIds: entry.lastRunRoutineIds,
          },
        },
      };
    }
  } catch {
    // ignore parse failures
  }

  return emptyCacheStore();
};

/** Drop entries older than yesterday so the store stays small. */
export const prunePlanCacheStore = (store: AutoAllPlanCacheStore): AutoAllPlanCacheStore => {
  const keepFrom = dayjs().subtract(1, 'day').format('YYYY-MM-DD');
  const plansByDate = Object.fromEntries(
    Object.entries(store.plansByDate).filter(([date]) => date >= keepFrom)
  );
  return { version: 2, plansByDate };
};

export const getPlanCacheForDate = (
  store: AutoAllPlanCacheStore,
  date: string
): AutoAllPlanCacheEntry | null => store.plansByDate[date] ?? null;

export const hasPlanCacheForDate = (store: AutoAllPlanCacheStore, date: string): boolean => {
  const entry = getPlanCacheForDate(store, date);
  if (!entry) {
    return false;
  }
  if (entry.pendingJobId) {
    return true;
  }
  return (
    (Array.isArray(entry.plannedTypes) && entry.plannedTypes.length > 0) ||
    (Array.isArray(entry.schedules) && entry.schedules.length > 0)
  );
};

const withoutPendingFields = (
  entry: AutoAllPlanCacheEntry
): Omit<AutoAllPlanCacheEntry, 'stashedEntry' | 'pendingJobId'> => {
  const { pendingJobId: _pendingJobId, stashedEntry: _stashedEntry, ...visible } = entry;
  return visible;
};

/** Hide this date's saved plan for the duration of `jobId`. A refresh reads
 * this cache directly, so leaving the previous schedules here shows that
 * finished run instead of the one still generating. */
export const hidePlanCacheBehindPendingJob = (date: string, jobId: string): void => {
  const store = readPlanCacheStore();
  const existing = getPlanCacheForDate(store, date);
  if (existing?.pendingJobId === jobId) {
    return;
  }
  const stashedEntry = existing?.pendingJobId
    ? existing.stashedEntry
    : existing
      ? withoutPendingFields(existing)
      : undefined;
  const hidden: AutoAllPlanCacheEntry = {
    lastRunAtISO: existing?.lastRunAtISO ?? new Date().toISOString(),
    signature: existing?.signature ?? '',
    schedules: [],
    plannedTypes: existing?.plannedTypes,
    summary: null,
    acceptedIds: [],
    autoAcceptedIds: [],
    discardedIds: [],
    lastRunTodoIds: existing?.lastRunTodoIds,
    lastRunRoutineIds: existing?.lastRunRoutineIds,
    pendingJobId: jobId,
    ...(stashedEntry ? { stashedEntry } : {}),
  };
  writePlanCacheStore({
    version: 2,
    plansByDate: {
      ...store.plansByDate,
      [date]: hidden,
    },
  });
};

/** Put back the plan hidden by `hidePlanCacheBehindPendingJob` (cancel, or a
 * job that never registered on the server). */
export const restorePlanCacheAfterPendingJob = (date: string): void => {
  const store = readPlanCacheStore();
  const existing = getPlanCacheForDate(store, date);
  if (!existing?.pendingJobId) {
    return;
  }
  const plansByDate = { ...store.plansByDate };
  if (existing.stashedEntry) {
    plansByDate[date] = existing.stashedEntry;
  } else {
    delete plansByDate[date];
  }
  writePlanCacheStore({ version: 2, plansByDate });
};

/** Drop the pending flag without putting the previous plan back on screen. */
export const finishPendingPlanCacheJob = (date: string): void => {
  const store = readPlanCacheStore();
  const existing = getPlanCacheForDate(store, date);
  if (!existing?.pendingJobId) {
    return;
  }
  const { pendingJobId: _pendingJobId, stashedEntry: _stashedEntry, ...visible } = existing;
  writePlanCacheStore({
    version: 2,
    plansByDate: {
      ...store.plansByDate,
      [date]: visible,
    },
  });
};

export const findPendingPlanCacheJob = (): { date: string; jobId: string } | null => {
  const store = readPlanCacheStore();
  for (const [date, entry] of Object.entries(store.plansByDate)) {
    if (entry?.pendingJobId) {
      return { date, jobId: entry.pendingJobId };
    }
  }
  return null;
};

export const readPlanCacheStore = (): AutoAllPlanCacheStore => {
  try {
    return parseCacheStore(safeLocalStorage.getItem(AUTO_ALL_PLAN_STORAGE_KEY));
  } catch {
    return emptyCacheStore();
  }
};

export const writePlanCacheStore = (store: AutoAllPlanCacheStore): void => {
  try {
    safeLocalStorage.setItem(AUTO_ALL_PLAN_STORAGE_KEY, JSON.stringify(prunePlanCacheStore(store)));
  } catch {
    // ignore storage failures
  }
};

/**
 * Serialize Plan My Day cache writes so concurrent persist/accept/discard
 * read-modify-write cycles cannot drop each other's date entries.
 */
let planCacheWriteChain: Promise<void> = Promise.resolve();

const enqueuePlanCacheWrite = <T>(task: () => Promise<T> | T): Promise<T> => {
  const run = planCacheWriteChain.then(task, task);
  planCacheWriteChain = run.then(
    () => undefined,
    () => undefined
  );
  return run;
};

/** Test-only: reset the write queue between cases. */
export const __resetPlanCacheWriteChainForTests = () => {
  planCacheWriteChain = Promise.resolve();
};

export const persistPlanCacheForDate = async (
  date: string,
  entry: AutoAllPlanCacheEntry,
  options?: { replacesPendingJobId?: string }
): Promise<void> => {
  await enqueuePlanCacheWrite(() => {
    const store = readPlanCacheStore();
    const existing = getPlanCacheForDate(store, date);
    // A refresh reads this entry directly. An older persist queued before the
    // hide must not put the previous schedules back while the job is in flight.
    if (existing?.pendingJobId && existing.pendingJobId !== options?.replacesPendingJobId) {
      return;
    }
    writePlanCacheStore({
      version: 2,
      plansByDate: {
        ...store.plansByDate,
        [date]: entry,
      },
    });
  });
};

export const updatePlanCacheForDate = async (
  date: string,
  updater: (existing: AutoAllPlanCacheEntry | null) => AutoAllPlanCacheEntry | null
): Promise<void> => {
  await enqueuePlanCacheWrite(() => {
    const store = readPlanCacheStore();
    const existing = getPlanCacheForDate(store, date);
    const next = updater(existing);
    if (!next) {
      return;
    }
    // Keep the in-flight hide. Merges that rebuild an entry from scratch drop
    // pendingJobId and would paint the previous schedules on refresh.
    const entry = existing?.pendingJobId
      ? {
          ...next,
          schedules: [],
          summary: null,
          acceptedIds: [],
          autoAcceptedIds: [],
          discardedIds: [],
          pendingJobId: existing.pendingJobId,
          ...(existing.stashedEntry ? { stashedEntry: existing.stashedEntry } : {}),
        }
      : next;
    writePlanCacheStore({
      version: 2,
      plansByDate: {
        ...store.plansByDate,
        [date]: entry,
      },
    });
  });
};

const uniqueStringIds = (left?: string[], right?: string[]): string[] =>
  Array.from(new Set([...(left ?? []), ...(right ?? [])]));

const mergePlanSchedulesByReplacedTypes = ({
  previousSchedules,
  nextSchedules,
  replacedTypes,
}: {
  previousSchedules: ScheduledAction[];
  nextSchedules: ScheduledAction[];
  replacedTypes: AllPlanScheduleType[];
}): ScheduledAction[] => {
  const replaced = new Set(replacedTypes);
  return dedupeAllPlanScheduledActions([
    ...previousSchedules.filter((schedule) => {
      const type = resolveAllPlanScheduleType(schedule);
      return !isAllPlanScheduleType(type) || !replaced.has(type);
    }),
    ...nextSchedules,
  ]);
};

export const mergeDailyDefaultsIntoPlanCacheEntry = ({
  existing,
  nextSchedules,
  nextPlannedTypes,
  nextSummary,
  nextAcceptedIds,
  nextAutoAcceptedIds,
  nextDiscardedIds,
}: {
  existing: AutoAllPlanCacheEntry | null;
  nextSchedules: ScheduledAction[];
  nextPlannedTypes: AllPlanScheduleType[];
  nextSummary?: string | null;
  nextAcceptedIds?: string[];
  nextAutoAcceptedIds?: string[];
  nextDiscardedIds?: string[];
}): AutoAllPlanCacheEntry => {
  const previousSchedules = Array.isArray(existing?.schedules)
    ? (existing.schedules as ScheduledAction[])
    : [];
  const previousPlannedTypes = Array.isArray(existing?.plannedTypes)
    ? existing.plannedTypes.filter(isAllPlanScheduleType)
    : [];

  return {
    signature: existing?.signature ?? '',
    lastRunAtISO: new Date().toISOString(),
    schedules: mergePlanSchedulesByReplacedTypes({
      previousSchedules,
      nextSchedules,
      replacedTypes: ['meal', 'sleep'],
    }) as unknown[],
    plannedTypes: Array.from(new Set([...previousPlannedTypes, ...nextPlannedTypes])),
    summary: existing?.summary ?? nextSummary ?? null,
    acceptedIds: uniqueStringIds(existing?.acceptedIds, nextAcceptedIds),
    autoAcceptedIds: uniqueStringIds(existing?.autoAcceptedIds, nextAutoAcceptedIds),
    discardedIds: uniqueStringIds(existing?.discardedIds, nextDiscardedIds),
    ...(existing?.lastRunTodoIds ? { lastRunTodoIds: existing.lastRunTodoIds } : {}),
    ...(existing?.lastRunRoutineIds ? { lastRunRoutineIds: existing.lastRunRoutineIds } : {}),
  };
};

export const persistDailyDefaultsPlanCacheForDate = async (
  date: string,
  payload: {
    schedules: ScheduledAction[];
    plannedTypes: AllPlanScheduleType[];
    summary: string | null;
    acceptedIds: string[];
    autoAcceptedIds: string[];
    discardedIds: string[];
  }
): Promise<void> => {
  await updatePlanCacheForDate(date, (existing) =>
    mergeDailyDefaultsIntoPlanCacheEntry({
      existing,
      nextSchedules: payload.schedules,
      nextPlannedTypes: payload.plannedTypes,
      nextSummary: payload.summary,
      nextAcceptedIds: payload.acceptedIds,
      nextAutoAcceptedIds: payload.autoAcceptedIds,
      nextDiscardedIds: payload.discardedIds,
    })
  );
};

const resolveAllPlanScheduleType = (schedule: ScheduledAction): string => {
  if (schedule.eventFrom === 'activity') {
    if (schedule.routineFrom?.routineActivityId) {
      return 'routine';
    }
    if (schedule.todoFrom?.taskId) {
      return 'todo';
    }
    return 'calendar';
  }

  if (schedule.eventFrom === 'existing_calendar') {
    return 'calendar';
  }

  return schedule.eventFrom ?? 'calendar';
};

type LegacyPlanTypesCacheEntry = AutoAllPlanCacheEntry & {
  planTypes?: unknown;
};

/** Derive plannedTypes from a cache entry (plannedTypes, legacy planTypes, or schedules). */
export const resolvePlannedTypesFromCacheEntry = (
  entry: AutoAllPlanCacheEntry | null | undefined
): AllPlanScheduleType[] => {
  if (!entry) {
    return [];
  }

  if (Array.isArray(entry.plannedTypes)) {
    return entry.plannedTypes.filter(isAllPlanScheduleType);
  }

  const legacyPlanTypes = (entry as LegacyPlanTypesCacheEntry).planTypes;
  if (Array.isArray(legacyPlanTypes)) {
    return legacyPlanTypes.filter(isAllPlanScheduleType);
  }

  if (!Array.isArray(entry.schedules)) {
    return [];
  }

  const plannedFromSchedules: AllPlanScheduleType[] = [];
  entry.schedules.forEach((schedule) => {
    const scheduleType = resolveAllPlanScheduleType(schedule as ScheduledAction);
    if (isAllPlanScheduleType(scheduleType) && !plannedFromSchedules.includes(scheduleType)) {
      plannedFromSchedules.push(scheduleType);
    }
  });
  return plannedFromSchedules;
};

/**
 * Target date to restore into the Plan My Day atom after Home cache hydrate.
 * Prefer today when a today plan exists; otherwise keep a prior non-today target
 * (e.g. tomorrow) or shift a stale "today" target forward to tomorrow.
 */
export const resolveRestoredTargetDate = ({
  hasTodayPlan,
  today,
  tomorrow,
  previousTargetDate,
}: {
  hasTodayPlan: boolean;
  today: string;
  tomorrow: string;
  previousTargetDate?: string | null;
}): string | null => {
  if (hasTodayPlan) {
    return today;
  }
  if (previousTargetDate === today) {
    return tomorrow;
  }
  return previousTargetDate ?? null;
};

export const buildAllPlanCacheRestoreKey = ({
  today,
  todayEntry,
  tomorrow,
  tomorrowEntry,
}: {
  today: string;
  todayEntry: AutoAllPlanCacheEntry | null;
  tomorrow: string;
  tomorrowEntry: AutoAllPlanCacheEntry | null;
}): string =>
  [
    today,
    todayEntry?.lastRunAtISO ?? '',
    todayEntry?.signature ?? '',
    Array.isArray(todayEntry?.schedules) ? todayEntry.schedules.length : 0,
    tomorrow,
    tomorrowEntry?.lastRunAtISO ?? '',
    tomorrowEntry?.signature ?? '',
    Array.isArray(tomorrowEntry?.schedules) ? tomorrowEntry.schedules.length : 0,
  ].join('|');

export type AllPlanHomeRestorePatch = {
  schedules: ScheduledAction[];
  summary: string | null;
  acceptedIds: string[];
  autoAcceptedIds: string[];
  discardedIds: string[];
  targetDate: string | null;
  lastRunTodoIds: string[];
  lastRunRoutineIds: string[];
  statusLines: string[];
  isLoading: boolean;
  error: null;
};

export type AllPlanHomeRestoreResult = {
  restoreKey: string;
  hasAnyPlan: boolean;
  plannedTypes: AllPlanScheduleType[];
  patch: AllPlanHomeRestorePatch;
};

const emptyAllPlanHomeRestorePatch = (): AllPlanHomeRestorePatch => ({
  schedules: [],
  summary: null,
  acceptedIds: [],
  autoAcceptedIds: [],
  discardedIds: [],
  targetDate: null,
  lastRunTodoIds: [],
  lastRunRoutineIds: [],
  statusLines: [],
  isLoading: false,
  error: null,
});

const schedulesFromCacheEntry = (entry: AutoAllPlanCacheEntry | null): ScheduledAction[] => {
  // A pending run hides the previous result. `stashedEntry` is the old plan
  // and must not be rendered while that job is still generating.
  if (!entry || entry.pendingJobId || !Array.isArray(entry.schedules)) {
    return [];
  }
  return entry.schedules as ScheduledAction[];
};

/**
 * Build a single atom patch for Home Plan My Day cache restore (today + tomorrow).
 * Callers should no-op when restoreKey matches the last applied key.
 */
export const buildAllPlanHomeRestore = ({
  store,
  today,
  previousTargetDate,
}: {
  store: AutoAllPlanCacheStore;
  today: string;
  previousTargetDate?: string | null;
}): AllPlanHomeRestoreResult => {
  const tomorrow = dayjs(today).add(1, 'day').format('YYYY-MM-DD');
  const todayEntry = getPlanCacheForDate(store, today);
  const tomorrowEntry = getPlanCacheForDate(store, tomorrow);
  const hasTodayPlan = hasPlanCacheForDate(store, today);
  const hasTomorrowPlan = hasPlanCacheForDate(store, tomorrow);
  const restoreKey = buildAllPlanCacheRestoreKey({
    today,
    todayEntry,
    tomorrow,
    tomorrowEntry,
  });

  if (!hasTodayPlan && !hasTomorrowPlan) {
    return {
      restoreKey,
      hasAnyPlan: false,
      plannedTypes: [],
      patch: emptyAllPlanHomeRestorePatch(),
    };
  }

  const plannedTypes = hasTodayPlan ? resolvePlannedTypesFromCacheEntry(todayEntry) : [];
  const todaySchedules = schedulesFromCacheEntry(todayEntry);
  const tomorrowSchedules = schedulesFromCacheEntry(tomorrowEntry);
  const originalSchedules = [...todaySchedules, ...tomorrowSchedules];
  const dedupedSchedules = dedupeAllPlanScheduledActions(originalSchedules);

  const todayAccepted = Array.isArray(todayEntry?.acceptedIds) ? todayEntry.acceptedIds : [];
  const todayAutoAccepted = Array.isArray(todayEntry?.autoAcceptedIds)
    ? todayEntry.autoAcceptedIds
    : [];
  const todayDiscarded = Array.isArray(todayEntry?.discardedIds) ? todayEntry.discardedIds : [];
  const tomorrowAccepted = Array.isArray(tomorrowEntry?.acceptedIds)
    ? tomorrowEntry.acceptedIds
    : [];
  const tomorrowAutoAccepted = Array.isArray(tomorrowEntry?.autoAcceptedIds)
    ? tomorrowEntry.autoAcceptedIds
    : [];
  const tomorrowDiscarded = Array.isArray(tomorrowEntry?.discardedIds)
    ? tomorrowEntry.discardedIds
    : [];

  return {
    restoreKey,
    hasAnyPlan: true,
    plannedTypes,
    patch: {
      schedules: dedupedSchedules,
      summary: hasTodayPlan ? (todayEntry?.summary ?? null) : null,
      acceptedIds: remapAllPlanDecisionIdsForDedupedSchedules({
        originalSchedules,
        dedupedSchedules,
        decisionIds: [...todayAccepted, ...tomorrowAccepted],
      }),
      autoAcceptedIds: remapAllPlanDecisionIdsForDedupedSchedules({
        originalSchedules,
        dedupedSchedules,
        decisionIds: [...todayAutoAccepted, ...tomorrowAutoAccepted],
      }),
      discardedIds: remapAllPlanDecisionIdsForDedupedSchedules({
        originalSchedules,
        dedupedSchedules,
        decisionIds: [...todayDiscarded, ...tomorrowDiscarded],
      }),
      targetDate: resolveRestoredTargetDate({
        hasTodayPlan,
        today,
        tomorrow,
        previousTargetDate,
      }),
      lastRunTodoIds:
        hasTodayPlan && Array.isArray(todayEntry?.lastRunTodoIds) ? todayEntry.lastRunTodoIds : [],
      lastRunRoutineIds:
        hasTodayPlan && Array.isArray(todayEntry?.lastRunRoutineIds)
          ? todayEntry.lastRunRoutineIds
          : [],
      statusLines: [],
      isLoading: Boolean(todayEntry?.pendingJobId || tomorrowEntry?.pendingJobId),
      error: null,
    },
  };
};

/**
 * True when in-memory today-scoped state (plannedTypes) should update for this
 * target date. Unspecified date means "today". Future-date runs still persist under their
 * own cache key via persistPlanCacheForDate — this gate only protects today's UI fields.
 */
export const shouldUpdateTodayScopedPlanState = (
  targetDate?: string | number | Date | Dayjs | null
): boolean => {
  if (!targetDate) {
    return true;
  }
  const parsedDate = dayjs(targetDate);
  return parsedDate.isValid() && parsedDate.isSame(dayjs(), 'day');
};

/** Resolve a Plan My Day target to a YYYY-MM-DD cache key (defaults to today). */
export const resolveAllPlanCacheDateKey = (
  targetDate?: string | number | Date | Dayjs | null
): string => {
  if (!targetDate) {
    return dayjs().format('YYYY-MM-DD');
  }
  const parsed = dayjs(targetDate);
  return parsed.isValid() ? parsed.format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD');
};

/** Snapshot schedules + decisions owned by a single date key for cache persistence. */
export const buildPlanCacheSnapshotForDate = (
  state: {
    schedules: AllPlanDatedSchedule[];
    acceptedIds: string[];
    autoAcceptedIds: string[];
    discardedIds: string[];
    summary: string | null;
  },
  dateKey: string
) => {
  // Stamped planDate wins (selected date, including next-day sleep). Unstamped
  // after-midnight sleep still falls back to the previous calendar day.
  const schedules = state.schedules.filter((schedule) => scheduleFallsOnDate(schedule, dateKey));
  const scheduleIds = new Set(schedules.map((schedule, index) => getScheduleId(schedule, index)));

  return {
    schedules,
    summary: state.summary,
    acceptedIds: state.acceptedIds.filter((id) => scheduleIds.has(id)),
    autoAcceptedIds: state.autoAcceptedIds.filter((id) => scheduleIds.has(id)),
    discardedIds: state.discardedIds.filter((id) => scheduleIds.has(id)),
  };
};

/**
 * Avoid overwriting a good multi-date cache entry when a run fails or returns nothing
 * for the target date (error paths return empty schedules).
 */
export const shouldPersistPlanCacheSnapshot = (snapshot: { schedules: unknown[] }): boolean =>
  Array.isArray(snapshot.schedules) && snapshot.schedules.length > 0;

export type AllPlanAutoRunDecision =
  | { shouldRun: false }
  | { shouldRun: true; runKind: 'first' | 'empty-cache' };

/** Decide whether Home should auto-run Plan My Day for today. */
export const resolveAllPlanAutoRunDecision = ({
  store,
  today,
  autoRunOnFirstVisit,
  now = dayjs(),
}: {
  store: AutoAllPlanCacheStore;
  today: string;
  autoRunOnFirstVisit: boolean;
  now?: Dayjs;
}): AllPlanAutoRunDecision => {
  const todayEntry = getPlanCacheForDate(store, today);
  const hasTodayPlan = hasPlanCacheForDate(store, today);
  const lastRunAt = todayEntry?.lastRunAtISO ? dayjs(todayEntry.lastRunAtISO) : null;
  const hasRunToday = hasTodayPlan && !!lastRunAt?.isValid();
  const hasCachedSchedules =
    Array.isArray(todayEntry?.schedules) && todayEntry.schedules.length > 0;
  const isBeforeFourPM = now.hour() < 16;

  if (autoRunOnFirstVisit && !hasRunToday) {
    return { shouldRun: true, runKind: 'first' };
  }
  if (hasRunToday && !hasCachedSchedules && isBeforeFourPM) {
    return { shouldRun: true, runKind: 'empty-cache' };
  }
  return { shouldRun: false };
};

/** Build a cache entry from a date-scoped run snapshot. */
export const buildPlanCacheEntryFromSnapshot = ({
  snapshot,
  plannedTypes,
  signature,
  lastRunTodoIds,
  lastRunRoutineIds,
  lastRunAtISO = new Date().toISOString(),
}: {
  snapshot: {
    schedules: ScheduledAction[];
    summary: string | null;
    acceptedIds: string[];
    autoAcceptedIds: string[];
    discardedIds: string[];
  };
  plannedTypes: AllPlanScheduleType[];
  signature: string;
  lastRunTodoIds?: string[];
  lastRunRoutineIds?: string[];
  lastRunAtISO?: string;
}): AutoAllPlanCacheEntry => ({
  lastRunAtISO,
  signature,
  schedules: snapshot.schedules as unknown[],
  plannedTypes,
  summary: snapshot.summary ?? null,
  acceptedIds: snapshot.acceptedIds,
  autoAcceptedIds: snapshot.autoAcceptedIds,
  discardedIds: snapshot.discardedIds,
  ...(lastRunTodoIds ? { lastRunTodoIds } : {}),
  ...(lastRunRoutineIds ? { lastRunRoutineIds } : {}),
});
